import React, {
  useEffect,
  useState,
} from 'react';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { Helmet } from 'react-helmet-async';

import {
  AcademicCapIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { FireLoader } from '../../components/Loader';

const isSafeInternalPath = (path) => {
  return (
    typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//')
  );
};

const isSubscriptionPath = (path) => {
  if (!path) return false;

  return (
    path === '/subscribe_to_plan' ||
    path === '/app/pricing' ||
    path.startsWith('/app/checkout')
  );
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    login,
    authReady,
    isAuthenticated,
    hasPaidPlan,
    user, // added to access user role
  } = useAuth();

  const [showPassword, setShowPassword] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [rememberMe, setRememberMe] =
    useState(false);

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const redirectParameter =
    new URLSearchParams(location.search).get(
      'redirect'
    );

  // Redirect logic after auth is ready
  useEffect(() => {
    if (!authReady || !isAuthenticated) {
      return;
    }

    // --- Super Admin redirection ---
    if (user && user.role === 'super_admin') {
      // If redirect parameter points to a safe Super Admin route, use it
      if (
        isSafeInternalPath(redirectParameter) &&
        redirectParameter.startsWith('/super-admin/')
      ) {
        navigate(redirectParameter, { replace: true });
      } else {
        navigate('/super-admin/dashboard', { replace: true });
      }
      return;
    }

    // --- Regular user redirection ---
    if (!hasPaidPlan) {
      const destination =
        isSafeInternalPath(redirectParameter) &&
        isSubscriptionPath(redirectParameter)
          ? redirectParameter
          : '/subscribe_to_plan';

      navigate(destination, {
        replace: true,
      });

      return;
    }

    const destination =
      isSafeInternalPath(redirectParameter) &&
      !isSubscriptionPath(redirectParameter)
        ? redirectParameter
        : '/app/dashboard';

    navigate(destination, {
      replace: true,
    });
  }, [
    authReady,
    isAuthenticated,
    hasPaidPlan,
    redirectParameter,
    navigate,
    user, // added as dependency
  ]);

  useEffect(() => {
    const rememberedEmail =
      localStorage.getItem('rememberedEmail');

    if (rememberedEmail) {
      setFormData((previous) => ({
        ...previous,
        email: rememberedEmail,
      }));

      setRememberMe(true);
    }
  }, []);

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (
      !/\S+@\S+\.\S+/.test(formData.email)
    ) {
      nextErrors.email =
        'Enter a valid email address';
    }

    if (!formData.password) {
      nextErrors.password =
        'Password is required';
    } else if (formData.password.length < 6) {
      nextErrors.password =
        'Password must contain at least 6 characters';
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((previous) => ({
        ...previous,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const result = await login(
        formData.email,
        formData.password
      );

      if (!result.success) {
        return;
      }

      // Determine the logged-in user from the result
      const loggedInUser = result.user || result.data?.user;

      // --- Super Admin redirection after successful login ---
      if (loggedInUser && loggedInUser.role === 'super_admin') {
        toast.success('Welcome Super Admin!');
        navigate('/super-admin/dashboard', { replace: true });
        return;
      }

      if (rememberMe) {
        localStorage.setItem(
          'rememberedEmail',
          formData.email.trim().toLowerCase()
        );
      } else {
        localStorage.removeItem(
          'rememberedEmail'
        );
      }

      // Password is deliberately not stored in localStorage.
      localStorage.removeItem(
        'rememberedPassword'
      );

      toast.success('Login successful! 🚀');

      if (result.subscriptionRequired) {
        navigate('/subscribe_to_plan', {
          replace: true,
        });

        return;
      }

      navigate('/app/dashboard', {
        replace: true,
      });
    } catch (error) {
      console.error('Login error:', error);

      toast.error(
        'An unexpected error occurred. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Auth not settled yet =====
  // Render only the loader until auth finishes hydrating from storage.
  // This prevents the login form from flashing for users who are already
  // authenticated and about to be redirected to their dashboard.
  if (!authReady) {
    return <FireLoader fullScreen size="lg" text="Loading..." />;
  }

  return (
    <>
      <Helmet>
        <title>Login | PawaSchool</title>

        <meta
          name="description"
          content="Sign in to PawaSchool to manage your school."
        />

        <meta
          name="robots"
          content="noindex, follow"
        />
      </Helmet>

      {/*
        Phone-app layout:
        - 100dvh so the layout doesn't jump when mobile browser bars appear/disappear
        - safe-area padding so notched iPhones don't clip the content
        - on mobile the card fills the screen like a native login screen
        - on >=sm the classic centered card with gradient behind it
      */}
      <div
        className="
          min-h-[100dvh] w-full
          bg-white sm:bg-gradient-to-br sm:from-primary-50 sm:via-white sm:to-secondary-50
          flex items-stretch sm:items-center justify-center
          sm:p-4
        "
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <div className="w-full sm:max-w-md flex">
          <div
            className="
              w-full
              bg-white sm:rounded-2xl
              border-0 sm:border sm:border-slate-200
              p-6 sm:p-8
              shadow-none sm:shadow-sm
              flex flex-col justify-center
            "
          >
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mx-auto">
                <AcademicCapIcon className="w-8 h-8 text-white" />
              </div>

              <h1 className="mt-5 text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                PawaSchool
              </h1>

              <h2 className="text-xl font-semibold text-slate-800 mt-2">
                Welcome Back
              </h2>

              <p className="text-slate-500 mt-1">
                Sign in to continue
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>

                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    autoComplete="email"
                    inputMode="email"
                    className="w-full pl-10 pr-10 py-3 rounded-lg border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-base"
                  />

                  {errors.email && (
                    <ExclamationCircleIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  )}
                </div>

                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>

                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />

                  <input
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className="w-full pl-10 pr-12 py-3 rounded-lg border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none text-base"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (previous) => !previous
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={
                      showPassword
                        ? 'Hide password'
                        : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.password}
                  </p>
                )}
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) =>
                    setRememberMe(
                      event.target.checked
                    )
                  }
                  className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />

                Remember my email
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? 'Signing in...'
                  : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}